import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';

import { USER_ROLE_RENTER } from 'common/userRoles';

import { getApplicationFeeUsdString } from 'common/householdApplication/applicationHelpers';

import {
  SR_API_ACTION_HOUSEHOLD_PAY_APPLICATION,
  SR_API_ACTION_HOUSEHOLD_GET_APPLICATION_SUBMISSIONS,
} from 'api/actionTypes';

import { SCREEN_ID_APPLICATION_PAID } from 'client/constants/screenIds';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

import Navigator from 'client/actions/Navigator';
import payApplicationSubmissionAction from 'client/actions/Auth/payApplicationSubmission';
import loadApplicationSubmissionsAction from 'client/actions/Household/loadApplicationSubmissions';
import listingGetByIdAction from 'client/actions/Listing/getById';
import SrStripeCardForm from 'client/components/SrStripeCardForm';
import Navbar from 'client/components/Navbar';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_RENTER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  householdPayApplicationApiState: {
    action: SR_API_ACTION_HOUSEHOLD_PAY_APPLICATION,
  },
  householdGetApplicationSubmissionsApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_APPLICATION_SUBMISSIONS,
  },
})
@branch({
  currentUser: ['currentUser'],
  householdApplications: ['householdApplications'],
  householdApplicationSubmissions: ['householdApplicationSubmissions'],
  listing: ['listing'],
})
@withScrollToTop
@withScreenId(SCREEN_ID_APPLICATION_PAID)
class ApplicationSubmissionPaymentPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Application Submitted | Sunroom',
  });

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    householdApplications: PropTypes.array,
    householdApplicationSubmissions: PropTypes.array,
    householdPayApplicationApiState: PropTypes.object.isRequired,
    householdGetApplicationSubmissionsApiState: PropTypes.object.isRequired,
    listing: PropTypes.object,
    location: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { currentUser, location, dispatch } = this.props;
    const locationQuery = queryString.parse(location.search);
    const listingId = parseInt(locationQuery.listingId, 10);
    return Promise.all([
      dispatch(loadApplicationSubmissionsAction, {
        userId: currentUser.id,
      }),
      dispatch(listingGetByIdAction, listingId),
    ]);
  }

  _getApplicationId = () => {
    const { location } = this.props;
    const locationQuery = queryString.parse(location.search);
    return parseInt(locationQuery.applicationId, 10) || null;
  };

  _getApplication = () => {
    const { householdApplications } = this.props;
    const applicationId = this._getApplicationId();
    return householdApplications
      ? householdApplications.find(({ id }) => id === applicationId)
      : null;
  };

  _getApplicationSubmission = () => {
    const { householdApplicationSubmissions } = this.props;
    const householdApplication = this._getApplication();

    if (!householdApplication) {
      return null;
    }

    return (householdApplicationSubmissions || []).find(
      (submission) =>
        submission.householdApplicationId === householdApplication.id &&
        submission.householdApplicationCategory === householdApplication.category,
    );
  };

  _onPaymentFormSubmit = (source) => {
    const { dispatch, location } = this.props;

    const { listingId: listingIdStr } = queryString.parse(location.search);

    const householdApplication = this._getApplication();

    dispatch(payApplicationSubmissionAction, {
      source: source,
      listingId: parseInt(listingIdStr, 10),
      applicationId: householdApplication.id,
    })
      .then(() => {
        return dispatch(Navigator.replace, {
          to: `/application/payment/thank-you?category=${encodeURIComponent(
            householdApplication.category,
          )}`,
        });
      })
      .catch((error) => {
        log.error(error);
      });
  };

  render() {
    const {
      currentUser,
      listing,
      householdPayApplicationApiState,
      householdGetApplicationSubmissionsApiState,
    } = this.props;

    const householdApplicationSubmission = this._getApplicationSubmission();
    const householdApplication = this._getApplication();

    if (!listing || householdGetApplicationSubmissionsApiState.inProgress) {
      return <div className="loading-gif" />;
    }

    if (householdApplicationSubmission && householdApplicationSubmission.stripeChargeId) {
      return (
        <Redirect
          to={`/application/payment/thank-you?category=${encodeURIComponent(
            householdApplication.category,
          )}`}
        />
      );
    }

    if (!householdApplication) {
      return <Redirect to="/logout" />;
    }

    const applicationFeeUsdString = getApplicationFeeUsdString({
      applicationFee: listing.applicationFee,
      user: currentUser,
    });

    return (
      <div className="page-payApplicationFeeForm">
        <Navbar />
        <div className="formProgressive">
          <div className="contentContainer">
            <span className="headline">{`Pay $${applicationFeeUsdString} application fee`}</span>
            <span className="explainer">Please pay the Guarantor Application fee.</span>
            <div>
              <SrStripeCardForm
                onSubmit={this._onPaymentFormSubmit}
                isSubmitting={householdPayApplicationApiState.inProgress}
                submitErrorMessage={
                  householdPayApplicationApiState.error
                    ? 'Sorry, the card did not work. Please try another card.'
                    : null
                }
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ApplicationSubmissionPaymentPage.displayName = 'ApplicationSubmissionPaymentPage';

export default ApplicationSubmissionPaymentPage;
